
import { defineComponent } from 'vue'
import ModalDialog from './ModalDialog.vue'
import PrimaryButton from '../buttons/PrimaryButton.vue'
import Loader from '../Loader.vue'
import { AlertType } from '@/enums'
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { IAlgoliaUserModel, IWorkingSiteModel } from '@sultan/shared'
import Toggle from '@/components/Toggle.vue'
import Checkbox from '@/components/Checkbox.vue'
import { LeftArrow, SuccessIcon } from '@/components/icons'
import { dateValidator } from '@/validators'
import { DatePicker } from 'v-calendar'
import moment from 'moment'

export default defineComponent({
  name: 'AddEditWorkingSiteModalDialog',

  emits: ['close'],

  components: {
    ModalDialog, PrimaryButton, Loader, LeftArrow, SuccessIcon, Toggle, Checkbox, DatePicker
  },

  props: {
    modelValue: {
      type: Object as () => IWorkingSiteModel,
      required: false,
      default: undefined
    }
  },

  setup () {
    return { v$: useVuelidate() }
  },

  mounted: function (): void {
    if (this.modelValue !== undefined) {
      this.step1.id = this.modelValue.id
      this.step1.name = this.modelValue.name ?? ''
      // eslint-disable-next-line eqeqeq
      if (this.modelValue.address != undefined) {
        this.step1.address = this.modelValue.address.address ?? ''
      }
      this.step1.customer = this.modelValue.customer ?? ''
      this.step1.link = this.modelValue.link ?? ''
      this.step1.orderNumber = this.modelValue.orderNumber ?? ''
      this.step1.isSpecialWorkingSite = this.modelValue.isSpecialWorkingSite ?? false
      if (this.modelValue.startTs) {
        this.step1.startDate = moment.unix(this.modelValue.startTs).toDate()
      }
      if (this.modelValue.endTs) {
        this.step1.endDate = moment.unix(this.modelValue.endTs).toDate()
      }
    }
  },

  data () {
    return {
      step: 0,
      users: [],
      usersSearchFilter: '',
      step1: {
        id: '',
        name: '',
        address: '',
        lat: undefined,
        lng: undefined,
        startDate: (new Date()) as Date | null,
        endDate: null as Date | null,
        customer: '',
        link: '',
        orderNumber: '',
        isSpecialWorkingSite: false
      },
      step2: {
        userIds: [],
        foremanIds: []
      },
      loading: false
    }
  },

  computed: {
    // eslint-disable-next-line eqeqeq
    isEditing: function (): boolean { return this.modelValue != undefined },
    title: function (): string {
      if (this.step > 1) { return '' }
      return this.isEditing
        ? 'Stai aggiornando il cantiere'
        : 'Stai aggiungendo un nuovo cantiere'
    },
    subtitle: function (): string {
      if (this.step > 1) { return '' }
      if (this.isEditing) { return 'Modifica le seguenti informazioni' }
      switch (this.step) {
        case 0: return 'Aggiungi le seguenti informazioni'
        case 1: return `Aggiungi dipendenti al cantiere "${this.step1.name}"`
        default: return ''
      }
    },
    steps: function (): string {
      if (this.step > 1) { return '' }
      if (this.isEditing) { return 'Creazione cantiere' }
      return `Step ${this.step + 1} di 2`
    },
    unselectedUsers: function (): IAlgoliaUserModel[] {
      return this.users.filter((u: IAlgoliaUserModel) => {
        return !(this.step2.userIds as any).includes(u.id)
      })
    },
    selectedUsers: function (): IAlgoliaUserModel[] {
      return this.users.filter((u: IAlgoliaUserModel) => {
        return (this.step2.userIds as any).includes(u.id)
      })
    },
    wsaPlaceholderIsAddress: function (): boolean {
      // eslint-disable-next-line eqeqeq
      return this.step1.address != undefined && this.step1.address != ''
    },
    workingSiteAddressPlaceholder: function (): string {
      // eslint-disable-next-line eqeqeq
      return this.step1.address != undefined && this.step1.address != '' ? this.step1.address : 'Inserisci l\'indirizzo o il nome del Porto d\'imbarco'
    }
  },

  validations () {
    return {
      step1: {
        name: { required },
        address: { required },
        startDate: { dateValidator, required },
        endDate: { dateValidator }
      }
    }
  },

  methods: {
    isForeman: function (id: string): boolean {
      return (this.step2.foremanIds as any).includes(id)
    },
    onStep2: function () {
      // Validate the fields
      this.v$.step1.$touch()
      if (this.v$.step1.$error) {
        return
      }
      this.searchUsers()

      this.step = 1
    },
    onStep1: function () {
      console.log('onStep1')
      this.step = 0
    },
    onClose: function () {
      this.$emit('close')
    },

    // Step 1
    onGmapAutocompleteAddress: function (evt: any) {
      console.log('onGmapAutocompleteAddress', { evt })
      // eslint-disable-next-line eqeqeq
      if (evt != undefined) {
        this.step1.address = evt.formatted_address !== undefined ? evt.formatted_address : ''
        if (evt.geometry !== undefined && evt.geometry.location !== undefined) {
          this.step1.lat = evt.geometry.location.lat()
          this.step1.lng = evt.geometry.location.lng()
        } else {
          this.step1.lat = undefined
          this.step1.lng = undefined
        }
      } else {
        this.step1.address = ''
        this.step1.lat = undefined
        this.step1.lng = undefined
      }
    },

    // Step 2
    addUser: function (userId: string): void {
      (this.step2.userIds as any).push(userId)
    },
    removeUser: function (userId: string): void {
      this.step2.userIds = this.step2.userIds.filter((u) => u !== userId)
    },
    toggleForman: function (userId: string): void {
      console.log(`toggleForman - ${userId}`)
      if ((this.step2.foremanIds as any).includes(userId)) {
        this.step2.foremanIds = this.step2.foremanIds.filter(
          (id) => id !== userId
        )
      } else {
        (this.step2.foremanIds as any).push(userId)
      }
    },
    searchUsers: async function (): Promise<void> {
      this.users = await this.$store.dispatch('users/instantSearchUsers', {
        query: this.usersSearchFilter
      })
      console.debug('searchUsers', { users: this.users })
    },
    create: async function () {
      this.loading = true

      // Validate the fields
      this.v$.step1.$touch()
      if (this.v$.step1.$error) {
        this.loading = false
        return
      }

      try {
        const endTs = moment(this.step1.endDate as any)
        const result = await this.$store.dispatch(
          'workingSites/createNewWorkingSite',
          {
            name: this.step1.name,
            address: this.step1.address,
            startTs: moment(this.step1.startDate as any).unix(),
            endTs: endTs.isValid() ? endTs.unix() : null,
            customer: this.step1.customer,
            link: this.step1.link,
            orderNumber: this.step1.orderNumber,
            addressLatitude: this.step1.lat,
            addressLongitude: this.step1.lng,
            isSpecialWorkingSite: this.step1.isSpecialWorkingSite,
            userIds: this.step2.userIds,
            foremanIds: this.step2.foremanIds
          }
        )

        this.loading = false
        if (result === undefined) {
          await this.$store.dispatch('alerts/create', {
            message: 'creazione cantiere fallita',
            type: AlertType.error
          })
          return
        }

        await this.$store.dispatch('alerts/create', {
          message: 'creazione cantiere riuscita',
          type: AlertType.success
        })
        // Add a delay to ensure that the Algolia Index is updated
        setTimeout(() => {
          // Then we reload the users
          this.$store.dispatch('workingSites/reloadWorkingSites')
        }, 4000)
        // this.$emit('close')
        this.step = 2
      } catch (error) {
        console.error('error creating the working site', error)
        this.loading = false
        await this.$store.dispatch('alerts/create', {
          message: 'creazione cantiere fallita',
          type: AlertType.error
        })
      }
    },

    // Update
    update: async function () {
      this.loading = true

      // Validate the fields
      this.v$.$touch()
      if (this.v$.$error) {
        this.loading = false
        return
      }

      try {
        const endTs = moment(this.step1.endDate as any)
        const result = await this.$store.dispatch(
          'workingSites/updateWorkingSite',
          {
            id: this.step1.id,
            name: this.step1.name,
            address: this.step1.address,
            startTs: moment(this.step1.startDate as any).unix(),
            endTs: endTs.isValid() ? endTs.unix() : null,
            customer: this.step1.customer,
            link: this.step1.link,
            addressLatitude: this.step1.lat,
            addressLongitude: this.step1.lng,
            orderNumber: this.step1.orderNumber,
            isSpecialWorkingSite: this.step1.isSpecialWorkingSite
          }
        )

        this.loading = false
        if (!result) {
          await this.$store.dispatch('alerts/create', {
            message: 'aggiornamento cantiere fallito',
            type: AlertType.error
          })
          return
        }

        await this.$store.dispatch('alerts/create', {
          message: 'aggiornamento cantiere riuscito',
          type: AlertType.success
        })
        // Add a delay to ensure that the Algolia Index is updated
        setTimeout(() => {
          // Then we reload the users
          this.$store.dispatch('workingSites/reloadWorkingSites')
        }, 4000)
        this.$emit('close')
      } catch (error) {
        console.error('error updating the working site', error)
        this.loading = false
        await this.$store.dispatch('alerts/create', {
          message: 'aggiornamento cantiere fallito',
          type: AlertType.error
        })
      }
    }
  }
})
